import '@/style/dashboard.scss';
import { useEffect, useState } from 'react';
import { Button, Field, Header, Input, Message, Modal, Toast } from "react-frontier";
import { Dropdown } from "semantic-ui-react";
import { useNavigate, useParams } from "react-router-dom";
import { createDocument, getDocuments, updateDocument } from '@/Api';
import Validator from '@/Validator';
import SidebarItem from '@/components/SidebarItem';
import DocumentEdit from '@/components/DocumentEdit';
import type { Document } from "Classes";

enum MODALS{
   CREATED=1
}

const INITVALUE:Document={
   document_id: 0,
   title: '',
   status: 0
}

var Sidebar=(props: { project_id: number })=>{
   var navigate=useNavigate();
   var[modal,setModal]=useState<MODALS>(null);
   var[error,setError]=useState<string>(null);
   var[documents,setDocuments]=useState<Document[]>(null);
   var[document,setDocument]=useState<Document>(INITVALUE);
   var[modalPrompts,setModalPrompts]=useState<string[]>(null);

   var onOpenModal=(document?: Document)=>{
      setModalPrompts(null);
      if(document)setDocument(document);
      setModal(MODALS.CREATED);
   }

   var onCloseModal=()=>{
      setModal(null);
      setDocument(INITVALUE);
   }

   var get=(project_id: number)=>{
      getDocuments(project_id).then(res=>{
         if(res.error)return setError(res.message);

         setDocuments(res.data);
      }).catch(()=>{
         return setError('Ocurrio un error al consultar la informacion. (DC-G-1)');
      });
   };

   useEffect(()=>{
      get(props.project_id);
   },[props.project_id]);

   var onCreateDocument=(setLoading: (loading: boolean) => void)=>{
      setLoading(true);
      setModalPrompts(null);

      var{valid,prompts}=Validator(document,{
         title: [{ rule: 'empty', label: 'Nombre del documento' }],
      });
      if(!valid){
         setLoading(false);
         return setModalPrompts(prompts);
      }

      createDocument(props.project_id,document.title).then(res=>{
         if(res.error) return setModalPrompts([res.message]);

         setDocuments(prev=>[...prev||[],{ 
            title: document.title, 
            status: res.data.status, 
            document_id: res.data.document_id, 
         }]);

         Toast.success('Documento creado correctamente.');

         onCloseModal();
      }).catch(()=>{
         return setModalPrompts(['Ocurrio un error creando el documento. (DC-CRT-1)']);
      }).finally(()=>{
         setLoading(false);
      });
   };

   var onUpdateDocument=(setLoading: (loading: boolean) => void)=>{
      setLoading(true);
      setModalPrompts(null);

      var{valid,prompts}=Validator(document,{
         title: [{ rule: 'empty', label: 'Nombre del documento' }],
      });
      if(!valid){
         setLoading(false);
         return setModalPrompts(prompts);
      }

      updateDocument(document.document_id, document.title, document.status).then(res=>{
         if(res.error) return setModalPrompts([res.message]);

         get(props.project_id);

         Toast.success('Documento actualizado correctamente.');
         onCloseModal();
      }).catch(()=>{
         return setModalPrompts(['Ocurrio un error actualizando el documento. (DC-UPD-1)']);
      }).finally(()=>{
         setLoading(false);
      });
   };

   if(error)return <Header text={error} />;
   if(!documents && !error)return <Header loading />;

   return <>
      <Button text='Nuevo documento' iconName='plus' color='black' onClick={()=>onOpenModal()} />
      <div className="divider">Documentos ({documents?documents.length:0})</div>
      {documents.length<1?(
         <Header subtext={'Sin documentos'} />
      ):documents.map((p)=>(
         <SidebarItem 
            text={p.title} 
            key={p.document_id}
            iconName="folder-blank" 
            to={`/project/${props.project_id}`} 
            search={`?document_id=${p.document_id}`} 
            badge={
               <span>
                  {p.status===1?'Borrador':p.status===2?'Publico':'Archivado'}
               </span>
            }
            extra={
               <Dropdown 
                  icon="ellipsis vertical" 
                  className="icon" 
                  pointing="top right"
                  onClick={(e:any)=>{ e.preventDefault(); e.stopPropagation(); }}
               >
                  <Dropdown.Menu>
                     <Dropdown.Item 
                        text="Editar" 
                        icon="pencil" 
                        onClick={(e:any)=>{ 
                           e.preventDefault(); 
                           e.stopPropagation(); 
                           onOpenModal(p); 
                        }} 
                     />
                  </Dropdown.Menu>
               </Dropdown>
            }
         />
      ))}
      <div className="filler" />
		<SidebarItem
			extra
			text="Regresar"
			iconName="arrow-left"
         onClick={()=>navigate({pathname:'/dashboard'})}
		/>

      <Modal open={modal===MODALS.CREATED} size='tiny' onClose={onCloseModal}>
         <Modal.Header>{document ? 'Editar documento' : 'Crear nuevo documento'}</Modal.Header>
         <Modal.Content>
            <Input 
               label='Titulo del documento' 
               value={document.title} 
               onChange={(val)=>setDocument({ ...document, title: val })} 
            />
            {document.document_id ? (
               <Field label='Estatus'>
                  <Dropdown 

                     selection
                     value={document.status}
                     onChange={(_, { value })=>setDocument({ ...document, status: value as number })}
                     options={[
                        { text: 'Borrador', value: 1 },
                        { text: 'Público', value: 2 },
                        { text: 'Archivado', value: 3 }
                     ]}
                  />
               </Field>
            ) : null}
            <Message type='error' list={modalPrompts} />
         </Modal.Content>
         <Modal.Actions>
            <Button text='Guardar' color='black' onClick={document.document_id ? onUpdateDocument : onCreateDocument} />
            <Button text='Cancelar' onClick={onCloseModal} />
         </Modal.Actions>
      </Modal>
   </>
}


var Project=()=>{
   var params=useParams();

   return <main style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', width: '100%', height: '100vh' }}>
      <div className="sidebar-container">
         <Sidebar project_id={+params.project_id} />
      </div>
      <div style={{gridColumn: 'span 4'}} className="drawer">
         <DocumentEdit />
      </div>
   </main>
};

export default Project;
